import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import Card from "../common/components/Card";
import DefaultButton from "../common/components/DefaultButton";
import { Col, Row } from "../common/components/Grid";
import useClientConfig from "../common/hooks/clientConfigHook";
import useNetwork from "../common/hooks/networkHook";
import { deepEquals } from "../common/utils/objectUtilities";
import { updateJobDefault, useJobDefault } from "../reducers/jobSlice";
import EventDropdown from "./EventDropdown";
import LevelDropdown from "./LevelDropdown";
import SiteDropdown from "./SiteDropdown";
import TypeDropdown from "./TypeDropdown";
import TextInput from "../common/components/TextInput";
import { deepClone } from "../common/utils/objectUtilities";
import { useUpsertSetting } from "../common/hooks/settingsHooks";
import { useSettingState } from "../common/hooks/settingStorageHook";
import Checkbox from "../common/components/Checkbox";

const JobDefaults = ({ username }) => {
    const { t } = useTranslation();
    const network = useNetwork();
    const clientConfig = useClientConfig();
    const jobDefault = useJobDefault(username);
    const [newJobDefault, setNewJobDefault] = useState(deepClone(jobDefault));
    const isJobDefaultUpdated = useMemo(() => !deepEquals(jobDefault, newJobDefault), [jobDefault, newJobDefault]);

    useEffect(() => {
        setNewJobDefault(newJobDefault => {
            if (!deepEquals(jobDefault, newJobDefault)) {
                return deepClone(jobDefault);
            }
            return newJobDefault;
        })
    }, [jobDefault])

    const handleUpdateJobDefault = data => {
        const delta = {};
        ['site','level','type','problem','event','event_name','priority'].forEach(f => {
            if (Object.hasOwn(data, f)) {
                delta[f] = data[f];
            }
        });
        setNewJobDefault(newJobDefault => ({...newJobDefault, ...delta}));
    }

    const applyUpdateJobDefault = () => {
        updateJobDefault(network, username, newJobDefault);
    }

    const jobDefaultFooterControls = <Row>
        <Col></Col>
        <Col xs='auto' className="pr-2"><DefaultButton disabled={!isJobDefaultUpdated} kind='clear' onClick={() => setNewJobDefault(deepClone(jobDefault))}></DefaultButton></Col>
        <Col xs='auto'><DefaultButton kind="apply" disabled={!isJobDefaultUpdated} onClick={applyUpdateJobDefault}></DefaultButton></Col>
    </Row>

    return <>
        { !clientConfig.epcrOnly && !clientConfig.trackerOnly && <Card title={t('defaultJobFields')} footerControls={jobDefaultFooterControls}>
            { clientConfig.enableSites && <SiteDropdown wildcard={t('noneWildcard')} site={newJobDefault.site} level={newJobDefault.level} type={newJobDefault.type} onChange={handleUpdateJobDefault}></SiteDropdown> }
            { clientConfig.enableLevels && <LevelDropdown wildcard={t('noneWildcard')} site={newJobDefault.site} level={newJobDefault.level} onChange={handleUpdateJobDefault}></LevelDropdown> }
            <TypeDropdown wildcard={t('noneWildcard')} site={newJobDefault.site} level={newJobDefault.level} type={newJobDefault.type} onChange={handleUpdateJobDefault}></TypeDropdown>
            { clientConfig.enableEvents === true && <EventDropdown wildcard={t('noneWildcard')} site={newJobDefault.site} level={newJobDefault.level} type={newJobDefault.type} event={newJobDefault.event} onChange={handleUpdateJobDefault}></EventDropdown> }
            { clientConfig.enableEvents === 'freetext' && <TextInput value={newJobDefault.event_free} placeholder={t('event_one')} onChange={event_free => handleUpdateJobDefault({ event_free })} maxLength={128}></TextInput>}
        </Card> }
    </>
}

const LogDefaults = ({ username }) => {
    const { t } = useTranslation();
    const upsertSetting = useUpsertSetting();
    const clientConfig = useClientConfig();
    const logDefault = useMemo(() => clientConfig.logDefault ?? {}, [clientConfig.logDefault]);
    const [newLogDefault, setNewLogDefault] = useState(deepClone(logDefault));
    const isLogDefaultUpdated = useMemo(() => !deepEquals(logDefault, newLogDefault), [logDefault, newLogDefault]);

    useEffect(() => {
        setNewLogDefault(newLogDefault => {
            if (!deepEquals(logDefault, newLogDefault)) {
                return deepClone(logDefault);
            }
            return newLogDefault;
        })
    }, [logDefault])

    const handleUpdateLogDefault = data => {
        const delta = {};
        ['site','level','type'].forEach(f => {
            if (Object.hasOwn(data, f)) {
                delta[f] = data[f];
            }
        });
        setNewLogDefault(newJobDefault => ({...newJobDefault, ...delta}));
    }

    const applyUpdateLogDefault = () => {
        upsertSetting(username, 'logDefault', newLogDefault);
    }

    const logDefaultFooterControls = <Row>
        <Col></Col>
        <Col xs='auto' className="pr-2"><DefaultButton disabled={!isLogDefaultUpdated} kind='clear' onClick={() => setNewLogDefault(deepClone(logDefault))}></DefaultButton></Col>
        <Col xs='auto'><DefaultButton kind="apply" disabled={!isLogDefaultUpdated} onClick={applyUpdateLogDefault}></DefaultButton></Col>
    </Row>

    return <>
        { !clientConfig.epcrOnly && !clientConfig.trackerOnly && clientConfig.enableLog && <Card title={t('defaultLogFields')} footerControls={logDefaultFooterControls}>
            { clientConfig.enableSites && <SiteDropdown wildcard={t('noneWildcard')} site={newLogDefault.site} level={newLogDefault.level} type={newLogDefault.type} onChange={handleUpdateLogDefault}></SiteDropdown> }
            { clientConfig.enableLevels && <LevelDropdown wildcard={t('noneWildcard')} site={newLogDefault.site} level={newLogDefault.level} onChange={handleUpdateLogDefault}></LevelDropdown> }
            <TypeDropdown wildcard={t('noneWildcard')} site={newLogDefault.site} level={newLogDefault.level} type={newLogDefault.type} onChange={handleUpdateLogDefault}></TypeDropdown>
        </Card> }
    </>
}

const DisplaySettings = () => {
    const { t } = useTranslation();
    const clientConfig = useClientConfig();
    const [showCrewDetails, setShowCrewDetails] = useSettingState('showCrewDetails', false);

    if (clientConfig.epcrOnly || clientConfig.trackerOnly) {
        return null;
    }

    return <Card title={t('displaySettings')}>
        <Checkbox placeholder={t('showCrewDetails')} checked={!!showCrewDetails} onChange={setShowCrewDetails}></Checkbox>
    </Card>
}

const UserSettings = ({ username }) => {
    return <>
        <JobDefaults username={username}></JobDefaults>
        <LogDefaults username={username}></LogDefaults>
        <DisplaySettings></DisplaySettings>
    </>
}

export default UserSettings;